/**
 * Network connection detection and monitoring
 * Detects online/offline status and triggers sync when connection returns
 */

type ConnectionCallback = (isOnline: boolean) => void;

const connectionListeners = new Set<ConnectionCallback>();

/**
 * Check if browser is currently online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Test if server is actually reachable (navigator.onLine can be unreliable)
 * Pings the API to confirm real connectivity
 */
export async function testServerReachability(timeoutMs = 5000): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    // Use the actual Worker API URL
    const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8787";

    // Try to fetch a lightweight endpoint
    const response = await fetch(`${apiUrl}/api/v1/customers?limit=1`, {
      method: "HEAD",
      signal: controller.signal,
      cache: "no-cache",
    });

    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    console.log("[Network] Server unreachable:", error);
    return false;
  }
}

/**
 * Register a callback to be notified of connection changes
 */
export function onConnectionChange(callback: ConnectionCallback): () => void {
  connectionListeners.add(callback);

  // Return unsubscribe function
  return () => {
    connectionListeners.delete(callback);
  };
}

/**
 * Initialize network monitoring
 * Sets up event listeners for online/offline events
 */
export function initializeNetworkMonitoring(): void {
  const handleOnline = async () => {
    console.log("[Network] Browser reports online");

    // Verify server is actually reachable before notifying
    const reachable = await testServerReachability();

    if (reachable) {
      console.log("[Network] Server reachable, triggering connection callbacks");
      connectionListeners.forEach((callback) => callback(true));
    } else {
      console.log("[Network] Server unreachable despite online status");
    }
  };

  const handleOffline = () => {
    console.log("[Network] Browser reports offline");
    connectionListeners.forEach((callback) => callback(false));
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  console.log("[Network] Network monitoring initialized");

  // Check initial status
  if (isOnline()) {
    testServerReachability().then((reachable) => {
      if (!reachable) {
        console.log("[Network] Initial check: Browser online but server unreachable");
      }
    });
  }
}

/**
 * Wait for online status (useful for retry logic)
 */
export function waitForOnline(timeoutMs?: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (isOnline()) {
      testServerReachability().then(resolve);
      return;
    }

    let timeoutId: NodeJS.Timeout | undefined;
    if (timeoutMs) {
      timeoutId = setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, timeoutMs);
    }

    const unsubscribe = onConnectionChange((online) => {
      if (online) {
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribe();
        resolve(true);
      }
    });
  });
}

/**
 * Get detailed network information (if available)
 */
export function getNetworkInfo(): {
  online: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
} {
  const info: ReturnType<typeof getNetworkInfo> = {
    online: isOnline(),
  };

  // @ts-expect-error - NetworkInformation API not in all browsers
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (connection) {
    info.effectiveType = connection.effectiveType;
    info.downlink = connection.downlink;
    info.rtt = connection.rtt;
    info.saveData = connection.saveData;
  }

  return info;
}
